import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  BugReport as BugReportIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LabelList
} from 'recharts';
import { format } from 'date-fns';
import { get, authGet, authPost, authPut } from '../../services/apiService';
import { getDefaultSprintByCurrentDate } from '../../services/sprintMetricsService';
import { isManager, isAdmin } from '../../services/authService';
import sprintSyncStatusService from '../../services/sprintSyncStatusService';
import SprintWorkItemsTable, { WORK_ITEM_COLORS, PRIORITY_COLORS } from './SprintWorkItemsTable';

// Custom tick component for multi-line X-axis labels
const CustomXAxisTick = (props) => {
  const { x, y, payload } = props;
  const lines = payload.value.split('\n');

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <text
          key={index}
          x={0}
          y={index * 14 + 16}
          textAnchor="middle"
          fill="#666"
          fontSize="11"
        >
          {line}
        </text>
      ))}
    </g>
  );
};

const SprintReportDashboard = ({ initialTeam: initialTeamFromUrl } = {}) => {
  // State management
  const [selectedSprint, setSelectedSprint] = useState('2025_S12_Jun04-Jun17');
  const [selectedTeam, setSelectedTeam] = useState('HQ-Core-SQ1');
  const [teams, setTeams] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noData, setNoData] = useState(false);

  // Data state
  const [sprintSummary, setSprintSummary] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [averageMetrics, setAverageMetrics] = useState(null);

  // Sync iterations state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Curated items pinned to the Sprint Review "Delivered Items" section (persisted per sprint+team).
  const [reportNotes, setReportNotes] = useState([]);
  const [notesMessage, setNotesMessage] = useState(null);

  // Table pagination

  // Table filters

  // Load initial data
  useEffect(() => {
    loadTeams();
    loadAvailableSprints();
  }, []);

  // Load sprint data when team or sprint changes
  useEffect(() => {
    if (selectedTeam && selectedSprint) {
      loadSprintData();
      loadReportNotes();
    }
  }, [selectedTeam, selectedSprint]);

  // Debug: Log sprintSummary when it changes
  useEffect(() => {
    if (sprintSummary) {
      console.log('Sprint Summary Updated:', sprintSummary);
      console.log('Scope Creep:', sprintSummary.scopeCreep);
      console.log('Test Case Count:', sprintSummary.workItemsByType['Test Case'] || 0);
      console.log('Adjusted Scope Creep:', sprintSummary.scopeCreep.addedItems - (sprintSummary.workItemsByType['Test Case'] || 0));
    }
  }, [sprintSummary]);

  // Recalculate averages when team changes
  useEffect(() => {
    if (selectedTeam) {
      calculateAverageMetrics();
    }
  }, [selectedTeam]);

  const loadTeams = async () => {
    try {
      const data = await get('/api/sprint-report/teams');

      if (data.success) {
        setTeams(data.data);
        if (data.data.length > 0) {
          if (initialTeamFromUrl) {
            const match = data.data.find(
              (t) =>
                t.id === initialTeamFromUrl ||
                (t.displayName && String(t.displayName).trim() === String(initialTeamFromUrl).trim())
            );
            if (match) setSelectedTeam(match.id);
            else setSelectedTeam(data.data[0].id);
          } else if (!selectedTeam) {
            setSelectedTeam(data.data[0].id);
          }
        }
      } else {
        setError('Failed to load teams');
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams');
    }
  };

  const loadAvailableSprints = async () => {
    try {
      const data = await get('/api/admin/sprints');

      if (data.success) {
        setSprints(data.data);
        // Default sprint: one whose date range is close to current date (current sprint, or most recent past/upcoming)
        if (data.data.length > 0) {
          const defaultSprintName = getDefaultSprintByCurrentDate(data.data);
          if (defaultSprintName) {
            setSelectedSprint(defaultSprintName);
          }
        }
      }
    } catch (err) {
      console.error('Error loading sprints:', err);
    }
  };

  const loadSprintData = async () => {
    setLoading(true);
    setError(null);
    setNoData(false);

    try {
      // Load sprint summary
      const summaryData = await get(`/api/sprint-report/summary/${selectedSprint}/${selectedTeam}`);

      if (summaryData.success) {
        // The server now correctly calculates scope creep excluding Test Case items
        const data = summaryData.data;

        // Log for debugging
        console.log('Scope creep items:', data.scopeCreep.addedItems);
        console.log('Scope creep percentage:', data.scopeCreep.percentage);
        console.log('Test Case count:', data.workItemsByType['Test Case'] || 0);

        // Also load detailed sprint data to get scope metrics
        const sprintDataResponse = await get(`/api/sprint-report/sprint-data/${selectedSprint}/${selectedTeam}`);
        if (sprintDataResponse.success && sprintDataResponse.data) {
          // Add scope metrics to the summary data
          data.scopeMetrics = {
            plannedStoryPoints: sprintDataResponse.data.plannedStoryPoints || 0,
            scopeAddedStoryPoints: sprintDataResponse.data.scopeAddedStoryPoints || 0,
            scopeRemovedStoryPoints: sprintDataResponse.data.scopeRemovedStoryPoints || 0,
            scopeFinalized: sprintDataResponse.data.scopeFinalized || false
          };
        }

        // Set the data directly
        setSprintSummary(data);
      } else {
        throw new Error(summaryData.message || 'Failed to load sprint summary');
      }

      // Load work items
      const workItemsData = await get(`/api/sprint-report/work-items/${selectedSprint}/${selectedTeam}`);

      if (workItemsData.success) {
        const nextWorkItems = Array.isArray(workItemsData?.data?.workItems)
          ? workItemsData.data.workItems
          : Array.isArray(workItemsData?.data)
            ? workItemsData.data
            : [];
        setWorkItems(nextWorkItems);
      } else {
        console.error('Work items API failed:', workItemsData.message);
        setWorkItems([]); // Ensure empty array if API fails
      }

      // Load bugs
      const bugsData = await get(`/api/sprint-report/bugs/${selectedSprint}/${selectedTeam}`);

      if (bugsData.success) {
        const nextBugs = Array.isArray(bugsData?.data?.bugs)
          ? bugsData.data.bugs
          : Array.isArray(bugsData?.data)
            ? bugsData.data
            : [];
        setBugs(nextBugs);
      } else {
        console.error('Bugs API failed:', bugsData.message);
        setBugs([]); // Ensure empty array if API fails
      }

    } catch (err) {
      console.error('Error loading sprint data:', err);
      if (err.message && (err.message.includes('404') || err.message.toLowerCase().includes('not found') || err.message.toLowerCase().includes('no sprint data'))) {
        setNoData(true);
        setSprintSummary(null);
        setWorkItems([]);
        setBugs([]);
      } else {
        setError(err.message || 'Failed to load sprint data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate average metrics from the last 6 sprints
  const calculateAverageMetrics = async () => {
    try {
      const data = await get(`/api/sprint-report/velocity/${selectedTeam}/6`);

      if (data.success && data.data.length > 0) {
        // Get the last 6 sprints or all available if less than 6
        const lastSixSprints = data.data.slice(-6);

        console.log('Last 6 sprints for average calculation:', lastSixSprints);

        // Calculate averages
        const avgVelocity = Math.round(
          lastSixSprints.reduce((sum, sprint) => sum + sprint.completedPoints, 0) / lastSixSprints.length
        );

        const avgCompletionRate = Math.round(
          lastSixSprints.reduce((sum, sprint) => {
            const rate = sprint.committedPoints > 0 ?
              (sprint.completedPoints / sprint.committedPoints) * 100 : 0;
            return sum + rate;
          }, 0) / lastSixSprints.length
        );

        // For each sprint, get the sprint summary to get the test case count
        const summarySettled = await Promise.allSettled(
          lastSixSprints.map(sprint =>
            get(`/api/sprint-report/summary/${sprint.sprintName}/${selectedTeam}`)
          )
        );
        const validSummaryResults = summarySettled
          .filter(r => r.status === 'fulfilled' && r.value && r.value.success)
          .map(r => r.value);

        // Use the server-provided scope creep percentage directly
        // The server now correctly calculates scope creep excluding Test Case items
        const avgScopeCreep = Math.round(
          validSummaryResults.reduce((sum, result) => {
            // Use the server-provided scope creep percentage
            return sum + (result.data.scopeCreep.percentage || 0);
          }, 0) / (validSummaryResults.length || 1)
        );

        console.log('Average scope creep from server values:', avgScopeCreep);

        // Get bug data for the last 6 sprints
        const bugSettled = await Promise.allSettled(
          lastSixSprints.map(sprint =>
            get(`/api/sprint-report/bugs/${sprint.sprintName}/${selectedTeam}`)
          )
        );
        const validBugResults = bugSettled
          .filter(r => r.status === 'fulfilled' && r.value && r.value.success)
          .map(r => r.value);

        const avgBugs = Math.round(
          validBugResults.reduce((sum, result) => sum + result.data.bugs.length, 0) /
          (validBugResults.length || 1)
        );

        // Calculate average bugs by priority
        const avgBugsByPriority = { P1: 0, P2: 0, P3: 0, P4: 0 };

        validBugResults.forEach(result => {
          const bugsByPriority = result.data.bugsByPriority;
          Object.keys(avgBugsByPriority).forEach(priority => {
            avgBugsByPriority[priority] += bugsByPriority[priority] || 0;
          });
        });

        Object.keys(avgBugsByPriority).forEach(priority => {
          avgBugsByPriority[priority] = Math.round(
            avgBugsByPriority[priority] / (validBugResults.length || 1)
          );
        });

        setAverageMetrics({
          velocity: avgVelocity,
          completionRate: avgCompletionRate,
          scopeCreep: avgScopeCreep,
          bugs: avgBugs,
          bugsByPriority: avgBugsByPriority
        });
      }
    } catch (err) {
      console.error('Error calculating average metrics:', err);
    }
  };

  // ── Curated sprint-report notes ─────────────────────────────────────────────
  const loadReportNotes = async () => {
    try {
      const data = await get(`/api/sprint-report/notes/${selectedSprint}/${selectedTeam}`);
      if (data.success) {
        setReportNotes(Array.isArray(data.data?.items) ? data.data.items : []);
      } else {
        setReportNotes([]);
      }
    } catch (err) {
      console.error('Error loading sprint report notes:', err);
      setReportNotes([]);
    }
    setNotesMessage(null);
  };

  // Pin a work item to the Delivered Items section of the Sprint Review tab.
  // Persists immediately (blank summary) so it shows up under the same team+sprint there.
  const handleAddToReport = async (item) => {
    const adoId = String(item.id);
    if (reportNotes.some((n) => String(n.adoId) === adoId)) return; // already added
    const updated = [...reportNotes, { adoId, title: item.title || '', summary: '' }];
    setReportNotes(updated);
    setNotesMessage(null);
    try {
      await authPut(`/api/sprint-report/notes/${selectedSprint}/${selectedTeam}`, { items: updated });
      setNotesMessage({ type: 'success', text: `Added ${adoId} to Delivered Items (Sprint Review).` });
    } catch (err) {
      console.error('Error adding item to delivered items:', err);
      setReportNotes(reportNotes); // revert on failure
      setNotesMessage({ type: 'error', text: `Failed to add ${adoId}: ${err.message}` });
    }
  };

  const isInReport = (id) => reportNotes.some((n) => String(n.adoId) === String(id));

  // Single "Refresh from ADO" action: re-sync the selected sprint+team from ADO
  // (work items, metrics, commit/develop-PR enrichment, and bugs-created), then reload.
  const handleRefreshFromAdo = async () => {
    if (!selectedTeam || !selectedSprint) return;
    setSyncing(true);
    setSyncResult(null);
    sprintSyncStatusService.start(`Sprint data is being refreshed for "${selectedSprint}"…`);
    try {
      const data = await authPost('/api/sprint-report/sync', { sprintName: selectedSprint, team: selectedTeam });
      setSyncResult({ ok: true, adoImport: data && data.adoImport });
      await loadSprintData();
      await loadReportNotes();
      calculateAverageMetrics();
    } catch (err) {
      setSyncResult({ error: err.message });
    } finally {
      setSyncing(false);
      sprintSyncStatusService.stop();
    }
  };

  const canRefreshFromAdo = isManager() || isAdmin();


  // Prepare chart data
  const workItemTypeData = sprintSummary ? Object.entries(sprintSummary.workItemsByType)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      name: type,
      value: count,
      color: WORK_ITEM_COLORS[type] || '#8884d8'
    })) : [];

  const bugPriorityData = sprintSummary ? Object.entries(sprintSummary.bugsByPriority)
    .filter(([_, count]) => count > 0)
    .map(([priority, count]) => ({
      name: priority,
      value: count,
      color: PRIORITY_COLORS[priority] || '#8884d8'
    })) : [];

  // A sprint only has "Missed" items once it has ended. While it is still in progress
  // (now <= sprint end date), incomplete planned/added items stay Planned/Added.
  const sprintEndDate = sprintSummary?.sprint?.endDate;
  const sprintEnded = sprintEndDate ? Date.now() > new Date(sprintEndDate).getTime() : false;


  if (loading && !sprintSummary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: '#1a202c',
            mb: 3,
            letterSpacing: '-0.5px'
          }}
        >
          Sprint Report Dashboard
        </Typography>

        {/* Controls */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Team</InputLabel>
              <Select
                value={selectedTeam}
                label="Team"
                onChange={(e) => setSelectedTeam(e.target.value)}
              >
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Sprint</InputLabel>
              <Select
                value={selectedSprint}
                label="Sprint"
                onChange={(e) => setSelectedSprint(e.target.value)}
              >
                {/* Available sprints from database (latest first) */}
                {sprints.map((sprint, index) => (
                  <MenuItem key={sprint.sprintName} value={sprint.sprintName}>
                    {sprint.sprintName} {index === 0 ? '(Latest)' : ''}
                  </MenuItem>
                ))}

                {/* Show message if no sprints available */}
                {sprints.length === 0 && (
                  <MenuItem disabled>
                    No synced sprints - Click "Sync ADO" to load data
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {canRefreshFromAdo && (
            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Re-fetch this team & sprint from ADO — work items, metrics, commits / develop-PR, and bugs created in the sprint">
                <span>
                  <Button
                    variant="contained"
                    startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                    onClick={handleRefreshFromAdo}
                    disabled={syncing || !selectedTeam || !selectedSprint}
                    fullWidth
                  >
                    {syncing ? 'Refreshing from ADO…' : 'Refresh from ADO'}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {syncResult?.ok && (
          <Alert
            severity={syncResult.adoImport && syncResult.adoImport.success === false ? 'warning' : 'success'}
            sx={{ mb: 2 }}
            onClose={() => setSyncResult(null)}
          >
            Refreshed <strong>{selectedSprint}</strong> / <strong>{selectedTeam}</strong> from ADO.
            {syncResult.adoImport && syncResult.adoImport.success === false
              ? ` (ADO fetch issue: ${syncResult.adoImport.error} — recomputed from existing data)`
              : (syncResult.adoImport
                  ? ` ADO items: ${syncResult.adoImport.total ?? 0} (created ${syncResult.adoImport.created ?? 0}, updated ${syncResult.adoImport.updated ?? 0}).`
                  : '')}
          </Alert>
        )}
        {syncResult?.error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncResult(null)}>
            Refresh failed: {syncResult.error}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {noData && !loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No sprint data found for <strong>{selectedSprint}</strong> / <strong>{selectedTeam}</strong>.
            Use the <strong>Sync Last 24 Iterations</strong> button above to pull data from ADO.
          </Alert>
        )}

        {/* Help message when no teams are available */}
        {teams.length === 0 && !loading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>No Teams Available:</strong> No teams are currently enabled for sprint reporting.
              Go to Admin Panel → Team Management to enable teams, or sync teams from sprint data first.
            </Typography>
          </Alert>
        )}

        {/* Help message when no data is available */}
        {!sprintSummary && !loading && teams.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Getting Started:</strong> Go to Admin Panel → Sprint Management to sync sprint data from Azure DevOps.
              Once synced, return here to view team-specific sprint reports and velocity trends.
            </Typography>
          </Alert>
        )}
      </Box>

      {sprintSummary && (
        <>
          {/* Metrics Cards in a horizontal row at the top */}
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              mb: 4,
              flexWrap: 'wrap',
              '& > *': { flex: 1, minWidth: '240px' }
            }}
          >
            {/* Velocity Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #90caf9', background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="overline" fontWeight="600" letterSpacing={1}>
                    Velocity
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(25, 118, 210, 0.15)' }}>
                    <TrendingUpIcon sx={{ color: '#1976d2', fontSize: '1.4rem' }} />
                  </Box>
                </Box>
                <Box display="flex" alignItems="stretch" gap={0}>
                  <Box flex={1} pr={2}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {averageMetrics ? averageMetrics.velocity : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600, opacity: 0.8 }}>
                      Avg Last 6 Sprints
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', bgcolor: 'rgba(25,118,210,0.2)', mx: 1 }} />
                  <Box flex={1} pl={2}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1565c0' }}>
                      {/* In-progress sprint: all SP taken into the sprint (committed).
                          Ended sprint: completed SP (velocity). */}
                      {sprintEnded
                        ? (sprintSummary.metrics.velocity ?? '—')
                        : (sprintSummary.metrics.totalStoryPoints ?? '—')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, opacity: 0.8 }}>
                      {sprintEnded ? 'This Sprint' : 'This Sprint (committed, in progress)'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Completion Rate Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #a5d6a7', background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="overline" fontWeight="600" letterSpacing={1}>
                    Completion Rate
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(56, 142, 60, 0.15)' }}>
                    <AssignmentIcon sx={{ color: '#388e3c', fontSize: '1.4rem' }} />
                  </Box>
                </Box>
                <Box display="flex" alignItems="stretch">
                  <Box flex={1} pr={2}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                      {averageMetrics ? `${averageMetrics.completionRate}%` : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#388e3c', fontWeight: 600, opacity: 0.8 }}>
                      Avg Last 6 Sprints
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', bgcolor: 'rgba(56,142,60,0.2)', mx: 1 }} />
                  <Box flex={1} pl={2}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {sprintSummary.metrics.completionRate}%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600, opacity: 0.8 }}>
                      This Sprint · {sprintSummary.metrics.completedWorkItems}/{sprintSummary.metrics.totalWorkItems}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Total Bugs Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #ffcc80', background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography color="textSecondary" variant="overline" fontWeight="600" letterSpacing={1}>
                    Total Bugs
                  </Typography>
                  <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(230, 81, 0, 0.15)' }}>
                    <BugReportIcon sx={{ color: '#e65100', fontSize: '1.4rem' }} />
                  </Box>
                </Box>
                <Box display="flex" alignItems="stretch">
                  <Box flex={1} pr={2}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#e65100' }}>
                      {averageMetrics ? averageMetrics.bugs : '—'}
                    </Typography>
                    <Box display="flex" gap={0.5} mt={0.5}>
                      <Chip label={`P1: ${averageMetrics ? averageMetrics.bugsByPriority.P1 : '—'}`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#ffebee', color: '#c62828' }} />
                      <Chip label={`P2: ${averageMetrics ? averageMetrics.bugsByPriority.P2 : '—'}`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#ef6c00' }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, opacity: 0.8 }}>
                      Avg Last 6 Sprints
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', bgcolor: 'rgba(230,81,0,0.2)', mx: 1 }} />
                  <Box flex={1} pl={2}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#bf360c' }}>
                      {bugs.length}
                    </Typography>
                    <Box display="flex" gap={0.5} mt={0.5}>
                      <Chip label={`P1: ${sprintSummary.bugsByPriority.P1}`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#ffebee', color: '#c62828' }} />
                      <Chip label={`P2: ${sprintSummary.bugsByPriority.P2}`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#ef6c00' }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#bf360c', fontWeight: 600, opacity: 0.8 }}>
                      This Sprint
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Scope Metrics - Only show if scope is finalized */}
            {sprintSummary.scopeMetrics && sprintSummary.scopeMetrics.scopeFinalized && (
              <>
                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" variant="overline" fontWeight="600" letterSpacing={1}>
                          Planned Points
                        </Typography>
                        <Typography variant="h4" sx={{ my: 1, fontWeight: 700, color: '#1a202c' }}>
                          {sprintSummary.scopeMetrics.plannedStoryPoints}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Finalized Scope
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(156, 39, 176, 0.1)' }}>
                        <TimelineIcon sx={{ color: '#9c27b0', fontSize: '1.8rem' }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" variant="overline" fontWeight="600" letterSpacing={1}>
                          Scope Added
                        </Typography>
                        <Typography variant="h4" sx={{ my: 1, fontWeight: 700, color: '#2196f3' }}>
                          +{sprintSummary.scopeMetrics.scopeAddedStoryPoints}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Story Points
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" variant="overline" fontWeight="600" letterSpacing={1}>
                          Scope Removed
                        </Typography>
                        <Typography variant="h4" sx={{ my: 1, fontWeight: 700, color: '#f44336' }}>
                          -{sprintSummary.scopeMetrics.scopeRemovedStoryPoints}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Story Points
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>

          {notesMessage && (
            <Alert severity={notesMessage.type} sx={{ mt: 3 }} onClose={() => setNotesMessage(null)}>
              {notesMessage.text}
            </Alert>
          )}

          {/* Work Items Table */}
          <SprintWorkItemsTable
            workItems={workItems}
            sprintEnded={sprintEnded}
            scopeMetrics={sprintSummary.scopeMetrics}
            onAddToReport={handleAddToReport}
            isInReport={isInReport}
            locked={!!sprintSummary?.sprint?.closed}
          />
        </>
      )}
    </Box>
  );
};

export default SprintReportDashboard;
